import "./index.css"

import Icon from "@utilities/Icon"
import { useId } from "@utilities/useId"
import classNames from "classnames"
import React, { FieldsetHTMLAttributes } from "react"

export type CheckboxGroupSize = "md" | "sm"

export interface CheckboxGroupProps {
  /** Optional description or error message rendered below the legend. */
  description?: string
  /** If true, adds aria-invalid and styles the description as an error. */
  invalid?: boolean
  /** The group label. Renders as <legend>. Required. */
  label: string
  /** Sets font size for the legend and description. */
  size?: CheckboxGroupSize
}

export type CheckboxGroupElementProps = Omit<
  FieldsetHTMLAttributes<HTMLFieldSetElement>,
  keyof CheckboxGroupProps
> &
  CheckboxGroupProps

/**
 * A checkbox group allows a user to select multiple items from a list of options.
 * Use it to group two or more checkboxes that share a common question or label.
 *
 * @component
 * @see {@link https://planningcenter.github.io/tapestry/?path=/docs/components-checkbox-group--docs | Storybook Documentation}
 */
export function CheckboxGroup({
  children,
  className,
  description,
  invalid,
  label,
  size = "md",
  ...restProps
}: CheckboxGroupElementProps) {
  const combinedClassName = classNames(
    "tds-checkbox-group",
    size === "sm" && "tds-checkbox-group--sm",
    className
  )
  const stableId = useId()

  const displayDescription =
    description || (invalid ? "One or more checkboxes are invalid." : undefined)
  const descriptionId = `tds-checkbox-group-${stableId}-description`

  return (
    <fieldset
      {...restProps}
      aria-describedby={displayDescription ? descriptionId : undefined}
      aria-invalid={invalid || undefined}
      className={combinedClassName}
    >
      <legend>{label}</legend>
      {displayDescription && (
        <p id={descriptionId} className="tds-checkbox-group-description">
          <Icon
            aria-hidden
            className="tds-checkbox-group-description-invalid-icon"
            symbol="general#exclamation-triangle"
          />
          {displayDescription}
        </p>
      )}
      <div className="tds-checkbox-group-fields">{children}</div>
    </fieldset>
  )
}
